import type { FreeScoutPreview } from "@hoffman-support/contracts";
import { useId, useRef, useState } from "react";
import {
  type FreeScoutRequester,
  freeScoutRequesters,
  freeScoutUrl,
  type HandoffSelection,
  handoffError,
} from "../freescout-api";

export function sourceDate(value: string | null) {
  if (!value) return "Not yet checked";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Date unavailable" : date.toLocaleString();
}

/** Render only reviewed metadata. Customer text, internal notes and attachments stay in FreeScout. */
export function FreeScoutSource({ preview }: { preview: FreeScoutPreview }) {
  const href = freeScoutUrl(preview.conversationUrl);
  return (
    <section
      aria-label="Email conversation"
      style={{ padding: "0.75rem", background: "#f4f6f8", marginBottom: "1rem" }}
    >
      <h3 style={{ marginTop: 0 }}>{preview.subject || "Conversation without a subject"}</h3>
      <p>FreeScout conversation #{preview.conversationId}</p>
      <p>
        Email sender: {preview.senderEmail ?? "Not available"}. This is reference information;
        verify and select the portal account below.
      </p>
      {href ? (
        <p>
          <a href={href} target="_blank" rel="noopener noreferrer">
            Open the conversation in FreeScout
          </a>
        </p>
      ) : null}
      <p>
        Only the selected message reference is linked. Message text, internal notes and attachments
        remain in FreeScout.
      </p>
    </section>
  );
}

export function RequesterChoices({
  requesters,
  selectedId,
  onSelect,
}: {
  requesters: FreeScoutRequester[];
  selectedId: string | undefined;
  onSelect: (requester: FreeScoutRequester) => void;
}) {
  return (
    <ul style={{ listStyle: "none", padding: 0 }}>
      {requesters.map((requester) => (
        <li key={requester.id} style={{ marginBottom: "0.75rem" }}>
          <button
            type="button"
            aria-pressed={selectedId === requester.id}
            onClick={() => onSelect(requester)}
          >
            {requester.displayName ? `${requester.displayName} — ` : ""}
            {requester.upn}
          </button>
          <br />
          <small>Account directory: {requester.tenantId}</small>
        </li>
      ))}
    </ul>
  );
}

export function FreeScoutSelection({
  preview,
  value,
  onChange,
  disabled = false,
}: {
  preview: FreeScoutPreview;
  value: HandoffSelection;
  onChange: (value: HandoffSelection) => void;
  disabled?: boolean;
}) {
  const id = useId();
  const [query, setQuery] = useState("");
  const [requesters, setRequesters] = useState<FreeScoutRequester[]>([]);
  const [searchState, setSearchState] = useState<"idle" | "loading" | "ready" | "failed">("idle");
  const [notice, setNotice] = useState("");
  const searchVersion = useRef(0);

  async function search() {
    if (query.trim().length < 2 || disabled || searchState === "loading") return;
    const version = ++searchVersion.current;
    setSearchState("loading");
    setNotice("");
    setRequesters([]);
    onChange({ ...value, requester: null, verification: "" });
    try {
      const result = await freeScoutRequesters(query);
      if (searchVersion.current !== version) return;
      setRequesters(result.requesters);
      setSearchState("ready");
    } catch (error) {
      if (searchVersion.current !== version) return;
      setSearchState("failed");
      setNotice(handoffError(error, "Portal accounts could not be found. Please try again."));
    }
  }

  return (
    <fieldset disabled={disabled} style={{ margin: "1rem 0", padding: "1rem" }}>
      <legend>Verify the source and requester</legend>
      <div style={{ marginBottom: "1rem" }}>
        <label htmlFor={`${id}-thread`}>Published customer message *</label>
        <br />
        <select
          id={`${id}-thread`}
          value={value.sourceThreadId ?? ""}
          required
          onChange={(event) =>
            onChange({
              ...value,
              sourceThreadId: event.target.value ? Number(event.target.value) : null,
            })
          }
        >
          <option value="">Choose a customer message…</option>
          {preview.sourceThreads.map((thread) => (
            <option key={thread.id} value={thread.id}>
              Message #{thread.id} · {sourceDate(thread.createdAt)}
            </option>
          ))}
        </select>
        {!preview.sourceThreads.length ? (
          <p role="status">
            No published customer message is available for handoff. Keep this conversation in
            FreeScout.
          </p>
        ) : null}
      </div>
      <div style={{ marginBottom: "1rem" }}>
        <label htmlFor={`${id}-search`}>Find a portal account by name or work email *</label>
        <br />
        <input
          id={`${id}-search`}
          type="search"
          value={query}
          maxLength={200}
          aria-describedby={`${id}-identity-help`}
          autoComplete="off"
          onChange={(event) => {
            searchVersion.current++;
            setQuery(event.target.value);
            setRequesters([]);
            setSearchState("idle");
            setNotice("");
            onChange({ ...value, requester: null, verification: "" });
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void search();
            }
          }}
        />{" "}
        <button
          type="button"
          disabled={query.trim().length < 2 || searchState === "loading"}
          onClick={() => void search()}
        >
          Find account
        </button>
        <p id={`${id}-identity-help`}>
          Choose the account you verified with the requester. They must have signed into this portal
          before you can select them.
        </p>
        {searchState === "loading" ? <p role="status">Finding portal accounts…</p> : null}
        {notice ? <p role="alert">{notice}</p> : null}
        {searchState === "ready" && requesters.length === 0 ? (
          <p role="status">
            No matching portal account. Ask the requester to sign in with their work account first,
            then search again. Keep their email in FreeScout until then.
          </p>
        ) : null}
        {requesters.length ? (
          <RequesterChoices
            requesters={requesters}
            selectedId={value.requester?.id}
            onSelect={(requester) => onChange({ ...value, requester, verification: "" })}
          />
        ) : null}
        {value.requester ? (
          <p role="status">
            <strong>Selected requester:</strong>{" "}
            {value.requester.displayName ?? value.requester.upn} ({value.requester.upn})
          </p>
        ) : null}
      </div>
      <label htmlFor={`${id}-verification`}>How did you verify this requester? *</label>
      <br />
      <textarea
        id={`${id}-verification`}
        rows={3}
        minLength={10}
        maxLength={500}
        required
        disabled={!value.requester}
        value={value.verification}
        style={{ width: "100%" }}
        aria-describedby={`${id}-verification-help`}
        onChange={(event) => onChange({ ...value, verification: event.target.value })}
      />
      <p id={`${id}-verification-help`}>
        Record the verification method in 10 to 500 characters. Do not enter passwords, security
        answers or verification codes.
      </p>
    </fieldset>
  );
}
